/**
 * The mark.
 *
 * Three circles overlapping into a cloud: a cloud one way, three people
 * standing close enough to touch the other. It is drawn in `currentColor` so it
 * takes the colour of whatever it sits in and needs no separate light and dark
 * artwork.
 *
 * Deliberately flat — no gradient, no glow, no glass. A mark that has to work
 * at sixteen pixels in a browser tab cannot afford any of them, and the idea is
 * clear enough without.
 */

interface LogoProps {
  /** Rendered size in pixels. The mark is drawn on a 32-unit grid. */
  size?: number;
  /** Shows the wordmark beside it. Off for tight spaces. */
  withWordmark?: boolean;
  className?: string;
}

export const PRODUCT_NAME = "MyFriends.Cloud";

export function Logo({
  size = 32,
  withWordmark = false,
  className,
}: LogoProps) {
  const mark = (
    <svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      fill="none"
      aria-hidden={withWordmark ? true : undefined}
      role={withWordmark ? undefined : "img"}
      aria-label={withWordmark ? undefined : PRODUCT_NAME}
      className={withWordmark ? undefined : className}
    >
      {/*
        Drawn back to front so the upper circle sits over the two below it,
        which is what turns three discs into one silhouette.
      */}
      <circle cx="11" cy="19" r="7" fill="currentColor" />
      <circle cx="21" cy="19" r="7" fill="currentColor" />
      <circle cx="16" cy="12" r="7.5" fill="currentColor" />
    </svg>
  );

  if (!withWordmark) return mark;

  return (
    <span className={["fc-logo", className].filter(Boolean).join(" ")}>
      {mark}
      {/*
        The two halves are weighted differently so the name reads as one word
        with an emphasis, rather than as a domain someone forgot to style.
      */}
      <span className="fc-logo__word">
        <strong>MyFriends</strong>
        <span className="fc-logo__tld">.Cloud</span>
      </span>
    </span>
  );
}
